import { useMemo } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { SelectSearch, SelectSearchBase } from "@telluscompany/design-system";
import { complexAdminScopedUserType } from "@shared/api";
import { useUserContext } from "@shared/contexts";

import { useAdminUsersQuery } from "../../features/users/api/admin-user";
import { AdminTenantRole } from "../../features/users/types";

interface BaseProps {
  label: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  clearable?: boolean;
  notFoundText?: string;
  /** Limite de opções carregadas. Default 100. */
  limit?: number;
  /**
   * Quando true, filtra para mostrar apenas usuários com role `user` no tenant
   * (fluxos de licenças/permissões granulares). A listagem do Admin já devolve
   * apenas role=user (owner/ownerViewer não aparecem).
   */
  onlyRoleUser?: boolean;
  /**
   * Quando true, exclui usuários `userType=tellus` da lista. Usado no fluxo de
   * alocação de licença: usuários `tellus` dispensam licença para módulos
   * operacionais, logo não são assignees elegíveis (backend rejeita com
   * 3.17.70). Independe do caller — vale inclusive para owner/ownerViewer.
   */
  excludeTellus?: boolean;
}

interface ControlledProps<TFieldValues extends FieldValues> extends BaseProps {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  value?: never;
  onChange?: never;
}

interface UncontrolledProps extends BaseProps {
  control?: never;
  name?: never;
  value: string | "" | undefined;
  onChange: (value: string) => void;
}

type UserComboboxProps<TFieldValues extends FieldValues = FieldValues> =
  | ControlledProps<TFieldValues>
  | UncontrolledProps;

/**
 * Combobox compartilhado para selecionar um usuário do tenant. As telas de
 * alocação de licenças e permissões granulares só podem alvejar usuários com
 * `role=user` no tenant — por isso o flag `onlyRoleUser` (default true).
 *
 * O label exibido é `name — email` para ajudar a diferenciar homônimos.
 *
 * Em modo uncontrolled (`value`/`onChange`), usa `SelectSearchBase`
 * diretamente para evitar o conflito entre o `useForm` interno e o estado
 * externo do consumidor (que provocava loop de requisições quando o pai
 * resetava o valor — por exemplo ao trocar o complexo na tela de permissões).
 */
export function UserCombobox<TFieldValues extends FieldValues = FieldValues>(
  props: UserComboboxProps<TFieldValues>,
) {
  const {
    label,
    placeholder,
    disabled,
    className,
    clearable,
    notFoundText,
    limit = 100,
    onlyRoleUser = true,
    excludeTellus = false,
  } = props;

  const { user: me } = useUserContext();
  const { data, isLoading } = useAdminUsersQuery({ page: 1, limit });

  // Defesa em profundidade: quando o caller é complexAdmin, só mostra usuários
  // do próprio userType (espelha o escopo que o backend já aplica na listagem).
  // owner/ownerViewer → null (sem filtro de userType).
  const scopedUserType = complexAdminScopedUserType(me?.tenantAccess);

  const options = useMemo(
    () =>
      (data?.data ?? [])
        .filter((user) =>
          onlyRoleUser ? user.role === AdminTenantRole.user : true,
        )
        .filter((user) =>
          scopedUserType ? user.userType === scopedUserType : true,
        )
        .filter((user) => (excludeTellus ? user.userType !== "tellus" : true))
        .map((user) => ({
          label: `${user.name} — ${user.email}`,
          value: user.id,
        })),
    [data, onlyRoleUser, scopedUserType, excludeTellus],
  );

  if ("control" in props && props.control && props.name) {
    return (
      <SelectSearch
        control={props.control}
        name={props.name}
        label={label}
        placeholder={placeholder}
        options={options}
        disabled={disabled || isLoading}
        className={className}
        clearable={clearable}
        notFoundText={notFoundText}
      />
    );
  }

  const { value, onChange } = props as UncontrolledProps;
  const isDisabled = disabled || isLoading;

  return (
    <div className={`grid gap-1 ${className ?? ""}`.trim()}>
      <label className={isDisabled ? "text-text-disabled" : undefined}>
        {label}
      </label>
      <SelectSearchBase
        value={value ?? ""}
        onChange={(next) => onChange(typeof next === "string" ? next : "")}
        placeholder={placeholder}
        options={options}
        disabled={isDisabled}
        clearable={clearable}
        notFoundText={notFoundText}
      />
    </div>
  );
}
